// ─────────────────────────────────────────────────────────────
// QUIZ PROFILE CONFIGURATION
// Configurable structure for quiz result profiles
// ─────────────────────────────────────────────────────────────

/**
 * Iris reference images for Question 5.
 * Each entry maps to a specific profile combination.
 */
export const IRIS_REFERENCE_IMAGES = {
    // Blue iris variants
    'iris-blue-open': { id: 'iris-blue-open', name: 'Azul Aberta', src: require("../../assets/images/iris/iris-blue-open.png"), fiber: 'open', color: 'blue' },
    'iris-blue-mixed': { id: 'iris-blue-mixed', name: 'Azul Mista', src: require("../../assets/images/iris/iris-blue-mixed.png"), fiber: 'mixed', color: 'blue' },
    'iris-blue-dense': { id: 'iris-blue-dense', name: 'Azul Densa', src: require("../../assets/images/iris/iris-blue-dense.png"), fiber: 'dense', color: 'blue' },
    'iris-blue-very-dense': { id: 'iris-blue-very-dense', name: 'Azul Muito Densa', src: require("../../assets/images/iris/iris-blue-very-dense.png"), fiber: 'very-dense', color: 'blue' },

    // Green iris variants
    'iris-green-open': { id: 'iris-green-open', name: 'Verde Aberta', src: require("../../assets/images/iris/iris-green-open.png"), fiber: 'open', color: 'green' },
    'iris-green-mixed': { id: 'iris-green-mixed', name: 'Verde Mista', src: require("../../assets/images/iris/iris-green-mixed.png"), fiber: 'mixed', color: 'green' },
    'iris-green-dense': { id: 'iris-green-dense', name: 'Verde Densa', src: require("../../assets/images/iris/iris-green-dense.png"), fiber: 'dense', color: 'green' },
    'iris-green-very-dense': { id: 'iris-green-very-dense', name: 'Verde Muito Densa', src: require("../../assets/images/iris/iris-green-very-dense.png"), fiber: 'very-dense', color: 'green' },

    // Light brown iris variants
    'iris-lbrown-open': { id: 'iris-lbrown-open', name: 'Castanho Claro Aberta', src: require("../../assets/images/iris/iris-light-brown-open.png"), fiber: 'open', color: 'light-brown' },
    'iris-lbrown-mixed': { id: 'iris-lbrown-mixed', name: 'Castanho Claro Mista', src: require("../../assets/images/iris/iris-light-brown-mixed.png"), fiber: 'mixed', color: 'light-brown' },
    'iris-lbrown-dense': { id: 'iris-lbrown-dense', name: 'Castanho Claro Densa', src: require("../../assets/images/iris/iris-light-brown-dense.png"), fiber: 'dense', color: 'light-brown' },
    'iris-lbrown-very-dense': { id: 'iris-lbrown-very-dense', name: 'Castanho Claro Muito Densa', src: require("../../assets/images/iris/iris-light-brown-very-dense.png"), fiber: 'very-dense', color: 'light-brown' },

    // Dark brown iris variants
    'iris-dbrown-open': { id: 'iris-dbrown-open', name: 'Castanho Escuro Aberta', src: require("../../assets/images/iris/iris-brown-open.png"), fiber: 'open', color: 'dark-brown' },
    'iris-dbrown-mixed': { id: 'iris-dbrown-mixed', name: 'Castanho Escuro Mista', src: require("../../assets/images/iris/iris-brown-mixed.png"), fiber: 'mixed', color: 'dark-brown' },
    'iris-dbrown-dense': { id: 'iris-dbrown-dense', name: 'Castanho Escuro Densa', src: require("../../assets/images/iris/iris-brown-dense.png"), fiber: 'dense', color: 'dark-brown' },
    'iris-dbrown-very-dense': { id: 'iris-dbrown-very-dense', name: 'Castanho Escuro Muito Densa', src: require("../../assets/images/iris/iris-brown-very-dense.png"), fiber: 'very-dense', color: 'dark-brown' },
};


/**
 * Profile definitions.
 * Each profile has cerebral, emotional, behavioral archetypes,
 * plus lists for strengths, tendencies, learning style, and decision style.
 */
export const QUIZ_PROFILES = {
    'visionary-stable': {
        id: 'visionary-stable',
        name: 'Visionário Estável',
        cerebralProfile: 'Visionário',
        emotionalProfile: 'Estável',
        behaviorProfile: 'Planejador',
        combination: 'Visionário + Estável + Planejador',
        strengths: [
            'Capacidade de visualizar o futuro e criar estratégias de longo prazo',
            'Estabilidade emocional diante de pressões e mudanças',
            'Pensamento inovador combinado com execução consistente',
            'Habilidade natural para inspirar e guiar equipes',
        ],
        tendencies: [
            'Busca por propósito e significado nas atividades',
            'Preferência por ambientes organizados e previsíveis',
            'Tendência a analisar antes de agir',
            'Valorização da consistência nos relacionamentos',
        ],
        learningStyle: [
            'Aprende melhor através de conceitos abstratos e visualizações',
            'Prefere estudar por mapas mentais e diagramas',
            'Retém informação conectando ideias de forma sistêmica',
        ],
        decisionStyle: [
            'Decide com base em visão estratégica de longo prazo',
            'Pondera os impactos com calma e racionalidade',
            'Conduz decisões equilibrando intuição e análise de dados',
        ],
    },

    'visionary-adaptive': {
        id: 'visionary-adaptive',
        name: 'Visionário Adaptável',
        cerebralProfile: 'Visionário',
        emotionalProfile: 'Adaptável',
        behaviorProfile: 'Comunicador',
        combination: 'Visionário + Adaptável + Comunicador',
        strengths: [
            'Flexibilidade para se adaptar a cenários imprevistos',
            'Capacidade de liderar pela comunicação e carisma',
            'Visão ampla e criativa para resolver problemas complexos',
            'Energia contagiante que mobiliza as pessoas ao redor',
        ],
        tendencies: [
            'Busca constante por novidades e experiências',
            'Facilidade em se conectar com pessoas de diferentes perfis',
            'Necessidade de variedade para manter o engajamento',
            'Tendência a iniciar múltiplos projetos simultaneamente',
        ],
        learningStyle: [
            'Aprende melhor por discussões, debates e troca de ideias',
            'Prefere métodos interativos e dinâmicos',
            'Assimila rapidamente conteúdos quando apresentados de forma visual',
        ],
        decisionStyle: [
            'Decide de forma ágil e confiante',
            'Usa a intuição alimentada pela experiência',
            'Adapta decisões conforme surgem novas informações',
        ],
    },

    'visionary-sensitive': {
        id: 'visionary-sensitive',
        name: 'Visionário Sensível',
        cerebralProfile: 'Visionário',
        emotionalProfile: 'Sensível',
        behaviorProfile: 'Cuidador',
        combination: 'Visionário + Sensível + Cuidador',
        strengths: [
            'Profundidade emocional e empatia elevada',
            'Capacidade de enxergar detalhes que outros ignoram',
            'Cuidado genuíno com o bem-estar dos outros',
            'Criatividade alimentada pela sensibilidade',
        ],
        tendencies: [
            'Necessidade de períodos de introspecção e silêncio',
            'Sensibilidade elevada a ambientes e estímulos externos',
            'Inclinação natural para profissões de cuidado e apoio',
            'Busca por relações profundas e significativas',
        ],
        learningStyle: [
            'Aprende melhor em ambientes calmos e acolhedores',
            'Prefere reflexão individual antes de compartilhar ideias',
            'Retém melhor informações com carga emocional ou narrativa',
        ],
        decisionStyle: [
            'Toma decisões considerando o impacto emocional nas pessoas',
            'Precisa de tempo para processar e sentir antes de decidir',
            'Valoriza o alinhamento entre valores pessoais e escolhas',
        ],
    },

    'strategic-adaptive': {
        id: 'strategic-adaptive',
        name: 'Estrategista Adaptável',
        cerebralProfile: 'Estratégico',
        emotionalProfile: 'Adaptável',
        behaviorProfile: 'Comunicador',
        combination: 'Estratégico + Adaptável + Comunicador',
        strengths: [
            'Pensamento estratégico combinado com flexibilidade',
            'Comunicação persuasiva e influente',
            'Capacidade de negociação e mediação',
            'Visão analítica para identificar oportunidades rapidamente',
        ],
        tendencies: [
            'Preferência por ambientes dinâmicos e desafiadores',
            'Habilidade para construir redes de contato estratégicas',
            'Inclinação para assumir posições de liderança',
            'Necessidade de desafios intelectuais constantes',
        ],
        learningStyle: [
            'Aprende melhor por estudos de caso e simulações práticas',
            'Prefere aprender com mentores e referências do mercado',
            'Retém melhor ao ensinar ou apresentar para outros',
        ],
        decisionStyle: [
            'Decide com base em dados, mas ajusta pela intuição',
            'Analisa riscos e recompensas com rapidez',
            'Busca o equilíbrio entre pragmatismo e inovação',
        ],
    },

    'strategic-sensitive': {
        id: 'strategic-sensitive',
        name: 'Estrategista Sensível',
        cerebralProfile: 'Estratégico',
        emotionalProfile: 'Sensível',
        behaviorProfile: 'Planejador',
        combination: 'Estratégico + Sensível + Planejador',
        strengths: [
            'Capacidade analítica combinada com inteligência emocional',
            'Planejamento detalhado e cuidadoso',
            'Percepção apurada de dinâmicas interpessoais',
            'Disciplina na execução de planos complexos',
        ],
        tendencies: [
            'Análise profunda antes de qualquer ação',
            'Preocupação com o impacto das decisões nos outros',
            'Preferência por processos claros e bem definidos',
            'Busca por segurança e estabilidade',
        ],
        learningStyle: [
            'Aprende melhor por leitura e pesquisa aprofundada',
            'Prefere ambientes de aprendizagem estruturados',
            'Retém através de anotações detalhadas e revisão',
        ],
        decisionStyle: [
            'Coleta o máximo de informações antes de decidir',
            'Considera fatores emocionais e lógicos simultaneamente',
            'Prefere decisões consensuais e bem fundamentadas',
        ],
    },

    'strategic-stable': {
        id: 'strategic-stable',
        name: 'Estrategista Estável',
        cerebralProfile: 'Estratégico',
        emotionalProfile: 'Estável',
        behaviorProfile: 'Executor',
        combination: 'Estratégico + Estável + Executor',
        strengths: [
            'Consistência e confiabilidade nas entregas',
            'Mente estratégica com execução disciplinada',
            'Resiliência emocional em cenários de pressão',
            'Foco em resultados mensuráveis',
        ],
        tendencies: [
            'Preferência por metas claras e métricas de sucesso',
            'Abordagem pragmática e orientada a resultados',
            'Valorização de rotinas e processos eficientes',
            'Inclinação para posições de gestão e operação',
        ],
        learningStyle: [
            'Aprende melhor pela prática e experimentação',
            'Prefere métodos passo-a-passo com resultados tangíveis',
            'Retém informação aplicando imediatamente no dia a dia',
        ],
        decisionStyle: [
            'Decide com base em dados e histórico comprovado',
            'Prefere decisões objetivas e pragmáticas',
            'Avalia cada decisão pelo impacto nos resultados finais',
        ],
    },

    'strategic-intense': {
        id: 'strategic-intense',
        name: 'Estrategista Intenso',
        cerebralProfile: 'Estratégico',
        emotionalProfile: 'Intenso',
        behaviorProfile: 'Executor',
        combination: 'Estratégico + Intenso + Executor',
        strengths: [
            'Determinação feroz para alcançar objetivos',
            'Capacidade de agir sob pressão intensa',
            'Mente analítica combinada com energia de execução',
            'Carisma e presença que impõe respeito',
        ],
        tendencies: [
            'Exigência elevada consigo e com os outros',
            'Busca pelo domínio e excelência em tudo que faz',
            'Energia emocional intensa nas conquistas e frustrações',
            'Impaciência com processos lentos e ineficientes',
        ],
        learningStyle: [
            'Aprende melhor através de desafios e competição',
            'Prefere aprender fazendo e corrigindo rápido',
            'Retém melhor quando há urgência e pressão positiva',
        ],
        decisionStyle: [
            'Decide rápido e assume as consequências',
            'Foca na ação e ajuste de rota',
            'Prioriza velocidade de execução sem perder a qualidade',
        ],
    },

    'analytic-stable': {
        id: 'analytic-stable',
        name: 'Analítico Estável',
        cerebralProfile: 'Analítico',
        emotionalProfile: 'Estável',
        behaviorProfile: 'Planejador',
        combination: 'Analítico + Estável + Planejador',
        strengths: [
            'Raciocínio lógico e metódico',
            'Capacidade de analisar dados complexos com precisão',
            'Equilíbrio emocional que favorece decisões sólidas',
            'Excelência em organização e planejamento',
        ],
        tendencies: [
            'Preferência por trabalhar com dados e evidências',
            'Meticulosidade e atenção a detalhes',
            'Cautela antes de tomar posição',
            'Busca por previsibilidade e controle',
        ],
        learningStyle: [
            'Aprende melhor por dados, gráficos e análises',
            'Prefere estudo individual e autônomo',
            'Retém melhor categorias e sistemas organizados',
        ],
        decisionStyle: [
            'Decide com base estritamente em evidências e lógica',
            'Evita decisões impulsivas',
            'Prioriza segurança e previsibilidade dos resultados',
        ],
    },

    'analytic-adaptive': {
        id: 'analytic-adaptive',
        name: 'Analítico Adaptável',
        cerebralProfile: 'Analítico',
        emotionalProfile: 'Adaptável',
        behaviorProfile: 'Comunicador',
        combination: 'Analítico + Adaptável + Comunicador',
        strengths: [
            'Capacidade de traduzir dados complexos para linguagem acessível',
            'Flexibilidade para ajustar análises ao contexto',
            'Ponte natural entre equipes técnicas e não-técnicas',
            'Pensamento crítico aliado à curiosidade',
        ],
        tendencies: [
            'Interesse por múltiplas áreas do conhecimento',
            'Facilidade em adaptar a comunicação para diferentes públicos',
            'Busca por eficiência em processos e sistemas',
            'Tendência a questionar o status quo',
        ],
        learningStyle: [
            'Aprende por múltiplos canais simultaneamente',
            'Prefere conteúdo que mescle teoria e aplicação',
            'Retém melhor ao explicar conceitos para outros',
        ],
        decisionStyle: [
            'Decide equilibrando análise e intuição prática',
            'Flexível para mudar de opinião diante de novos dados',
            'Busca soluções elegantes e eficientes',
        ],
    },

    'analytic-sensitive': {
        id: 'analytic-sensitive',
        name: 'Analítico Sensível',
        cerebralProfile: 'Analítico',
        emotionalProfile: 'Sensível',
        behaviorProfile: 'Cuidador',
        combination: 'Analítico + Sensível + Cuidador',
        strengths: [
            'Combinação rara de lógica e empatia',
            'Observa padrões emocionais nas pessoas com precisão',
            'Cuidado detalhista na execução de tarefas',
            'Profundidade na análise de situações complexas',
        ],
        tendencies: [
            'Perfeccionismo em atividades de cuidado e apoio',
            'Conexão emocional profunda com os problemas a resolver',
            'Necessidade de solitude para processar informações',
            'Busca por significado e contribuição genuína',
        ],
        learningStyle: [
            'Aprende em ritmo próprio com profundidade',
            'Prefere conteúdo com narrativas e conexão humana',
            'Retém melhor em ambientes de confiança e acolhimento',
        ],
        decisionStyle: [
            'Pondera entre a lógica e o impacto humano',
            'Busca decisões que tenham significado pessoal',
            'Investe tempo em considerar todas as perspectivas',
        ],
    },

    'analytic-intense': {
        id: 'analytic-intense',
        name: 'Analítico Intenso',
        cerebralProfile: 'Analítico',
        emotionalProfile: 'Intenso',
        behaviorProfile: 'Executor',
        combination: 'Analítico + Intenso + Executor',
        strengths: [
            'Capacidade excepcional de foco e concentração',
            'Determinação implacável para resolver problemas complexos',
            'Pensamento profundo e analítico sob pressão',
            'Energia intensa canalizada em resultados concretos',
        ],
        tendencies: [
            'Perfeccionismo elevado em tudo que empreende',
            'Intensidade emocional nas áreas de interesse',
            'Dificuldade em manter-se em atividades superficiais',
            'Busca pela excelência absoluta',
        ],
        learningStyle: [
            'Aprende por imersão total no assunto',
            'Prefere ir além da superfície e dominar cada conceito',
            'Retém melhor através de repetição e prática intensiva',
        ],
        decisionStyle: [
            'Decide com convicção após análise exaustiva',
            'Prefere dominar o tema antes de tomar posição',
            'Defende suas decisões com argumentos sólidos',
        ],
    },

    'explorer-stable': {
        id: 'explorer-stable',
        name: 'Explorador Estável',
        cerebralProfile: 'Explorador',
        emotionalProfile: 'Estável',
        behaviorProfile: 'Executor',
        combination: 'Explorador + Estável + Executor',
        strengths: [
            'Curiosidade natural equilibrada com disciplina',
            'Capacidade de explorar novas possibilidades de forma organizada',
            'Resiliência emocional para lidar com incertezas',
            'Versatilidade na adaptação a diferentes contextos',
        ],
        tendencies: [
            'Busca por experiências práticas e tangíveis',
            'Equilíbrio entre aventura e segurança',
            'Interesse por atividades que envolvam movimento e ação',
            'Preferência por aprender fazendo',
        ],
        learningStyle: [
            'Aprende por experiências práticas e hands-on',
            'Prefere explorar por tentativa e erro',
            'Retém melhor ao vivenciar situações reais',
        ],
        decisionStyle: [
            'Decide com base na experiência prática',
            'Equilibra ousadia com pragmatismo',
            'Ajusta decisões com naturalidade conforme o cenário evolui',
        ],
    },

    'explorer-adaptive': {
        id: 'explorer-adaptive',
        name: 'Explorador Adaptável',
        cerebralProfile: 'Explorador',
        emotionalProfile: 'Adaptável',
        behaviorProfile: 'Comunicador',
        combination: 'Explorador + Adaptável + Comunicador',
        strengths: [
            'Versatilidade extrema em diferentes ambientes',
            'Habilidade natural de networking e conexão',
            'Mente aberta para novas ideias e perspectivas',
            'Energia e entusiasmo contagiantes',
        ],
        tendencies: [
            'Necessidade constante de novos estímulos',
            'Facilidade em transitar entre diferentes grupos sociais',
            'Dificuldade com rotinas rígidas e repetitivas',
            'Interesse por viagens, culturas e experiências diversas',
        ],
        learningStyle: [
            'Aprende melhor explorando por conta própria',
            'Prefere conteúdo gamificado e interativo',
            'Retém melhor ao compartilhar experiências com outros',
        ],
        decisionStyle: [
            'Decide pela intuição e sensação do momento',
            'Flexível e aberto a mudanças de rumo',
            'Busca decisões que gerem novas experiências e crescimento',
        ],
    },

    'explorer-sensitive': {
        id: 'explorer-sensitive',
        name: 'Explorador Sensível',
        cerebralProfile: 'Explorador',
        emotionalProfile: 'Sensível',
        behaviorProfile: 'Cuidador',
        combination: 'Explorador + Sensível + Cuidador',
        strengths: [
            'Sensibilidade artística e percepção estética aguçada',
            'Capacidade de cuidar enquanto explora novas fronteiras',
            'Conexão profunda com a natureza e ambientes',
            'Empatia aliada à curiosidade pelo mundo',
        ],
        tendencies: [
            'Busca por experiências que alimentem a alma',
            'Sensibilidade elevada a ambientes e energias',
            'Inclinação para atividades criativas e artísticas',
            'Necessidade de conexão emocional com suas atividades',
        ],
        learningStyle: [
            'Aprende com conteúdo inspirador e estético',
            'Prefere ambientes de aprendizagem acolhedores',
            'Retém melhor através de experiências sensoriais',
        ],
        decisionStyle: [
            'Decide levando em conta o impacto emocional e sensorial',
            'Precisa sentir conexão com a decisão',
            'Busca decisões alinhadas com seus valores e crenças pessoais',
        ],
    },

    'explorer-intense': {
        id: 'explorer-intense',
        name: 'Explorador Intenso',
        cerebralProfile: 'Explorador',
        emotionalProfile: 'Intenso',
        behaviorProfile: 'Executor',
        combination: 'Explorador + Intenso + Executor',
        strengths: [
            'Energia incansável para explorar e conquistar',
            'Determinação feroz combinada com espírito aventureiro',
            'Capacidade de agir rapidamente em cenários novos',
            'Presença marcante e inesquecível',
        ],
        tendencies: [
            'Busca por adrenalina e desafios extremos',
            'Dificuldade em manter ritmo em atividades monótonas',
            'Intensidade emocional nas paixões e projetos',
            'Necessidade de estar em movimento constante',
        ],
        learningStyle: [
            'Aprende na prática e sob pressão',
            'Prefere imersão total e responde bem a desafios',
            'Retém melhor em situações de impacto e novidade',
        ],
        decisionStyle: [
            'Decide rapidamente e com ousadia',
            'Confia na intuição e na experiência adquirida',
            'Prefere ação imediata a planejamento prolongado',
        ],
    },
};

/* Map combinations to the archetypes */
export const PROFILE_MAP = {
    // Blue / Azul
    'blue-open-yes-yes': 'visionary-adaptive',
    'blue-open-yes-no': 'visionary-sensitive',
    'blue-open-no-yes': 'explorer-adaptive',
    'blue-open-no-no': 'visionary-stable',
    'blue-mixed-yes-yes': 'strategic-adaptive',
    'blue-mixed-yes-no': 'strategic-sensitive',
    'blue-mixed-no-yes': 'analytic-stable',
    'blue-mixed-no-no': 'analytic-stable',
    'blue-dense-yes-yes': 'analytic-intense',
    'blue-dense-yes-no': 'analytic-sensitive',
    'blue-dense-no-yes': 'analytic-stable',
    'blue-dense-no-no': 'analytic-stable',
    'blue-very-dense-yes-yes': 'analytic-intense',
    'blue-very-dense-yes-no': 'analytic-sensitive',
    'blue-very-dense-no-yes': 'analytic-stable',
    'blue-very-dense-no-no': 'analytic-stable',

    // Green / Verde
    'green-open-yes-yes': 'explorer-adaptive',
    'green-open-yes-no': 'explorer-sensitive',
    'green-open-no-yes': 'visionary-adaptive',
    'green-open-no-no': 'visionary-stable',
    'green-mixed-yes-yes': 'strategic-adaptive',
    'green-mixed-yes-no': 'strategic-sensitive',
    'green-mixed-no-yes': 'analytic-adaptive',
    'green-mixed-no-no': 'strategic-stable',
    'green-dense-yes-yes': 'analytic-intense',
    'green-dense-yes-no': 'analytic-sensitive',
    'green-dense-no-yes': 'analytic-stable',
    'green-dense-no-no': 'analytic-stable',
    'green-very-dense-yes-yes': 'analytic-intense',
    'green-very-dense-yes-no': 'analytic-sensitive',
    'green-very-dense-no-yes': 'analytic-stable',
    'green-very-dense-no-no': 'analytic-stable',

    // Light-brown / Castanho Claro
    'light-brown-open-yes-yes': 'explorer-adaptive',
    'light-brown-open-yes-no': 'explorer-intense',
    'light-brown-open-no-yes': 'visionary-adaptive',
    'light-brown-open-no-no': 'explorer-stable',
    'light-brown-mixed-yes-yes': 'strategic-intense',
    'light-brown-mixed-yes-no': 'strategic-sensitive',
    'light-brown-mixed-no-yes': 'analytic-adaptive',
    'light-brown-mixed-no-no': 'strategic-stable',
    'light-brown-dense-yes-yes': 'analytic-intense',
    'light-brown-dense-yes-no': 'analytic-sensitive',
    'light-brown-dense-no-yes': 'analytic-stable',
    'light-brown-dense-no-no': 'analytic-stable',
    'light-brown-very-dense-yes-yes': 'analytic-intense',
    'light-brown-very-dense-yes-no': 'analytic-sensitive',
    'light-brown-very-dense-no-yes': 'analytic-stable',
    'light-brown-very-dense-no-no': 'analytic-stable',

    // Dark-brown / Castanho Escuro
    'dark-brown-open-yes-yes': 'explorer-intense',
    'dark-brown-open-yes-no': 'explorer-sensitive',
    'dark-brown-open-no-yes': 'explorer-adaptive',
    'dark-brown-open-no-no': 'explorer-stable',
    'dark-brown-mixed-yes-yes': 'strategic-intense',
    'dark-brown-mixed-yes-no': 'strategic-sensitive',
    'dark-brown-mixed-no-yes': 'strategic-adaptive',
    'dark-brown-mixed-no-no': 'strategic-stable',
    'dark-brown-dense-yes-yes': 'analytic-intense',
    'dark-brown-dense-yes-no': 'analytic-sensitive',
    'dark-brown-dense-no-yes': 'analytic-adaptive',
    'dark-brown-dense-no-no': 'analytic-stable',
    'dark-brown-very-dense-yes-yes': 'analytic-intense',
    'dark-brown-very-dense-yes-no': 'analytic-sensitive',
    'dark-brown-very-dense-no-yes': 'analytic-adaptive',
    'dark-brown-very-dense-no-no': 'analytic-stable',
};

/**
 * Builds the key used to look up PROFILE_MAP.
 * Normalises Q3 (pigmentation) and Q4 (rings) values to yes/no.
 */
export function buildIrisKey(color, fiber, pigmentation, rings) {
    const colorMap = {
        'Azul': 'blue',
        'Verde': 'green',
        'Castanho Claro': 'light-brown',
        'Castanho Escuro': 'dark-brown',
    };
    const fiberMap = {
        'open': 'open',
        'mixed': 'mixed',
        'dense': 'dense',
        'very-dense': 'very-dense',
        'intermediate': 'mixed'
    };
    const pigNorm = pigmentation === 'Sim' ? 'yes' : 'no';
    const ringNorm = rings === 'Sim' ? 'yes' : 'no';

    const c = colorMap[color] || 'blue';
    const f = fiberMap[fiber] || 'mixed';
    return `${c}-${f}-${pigNorm}-${ringNorm}`;
}

/**
 * Resolves a profile from quiz answers.
 * Returns a profile object from QUIZ_PROFILES.
 */
export function resolveProfile(answers) {
    const { irisColor, fiberPattern, pigmentation, rings, selectedIrisModel } = answers;

    let finalFiber = fiberPattern;
    // Overwrite fiber if selected modeled provides stronger evidence
    if (selectedIrisModel && IRIS_REFERENCE_IMAGES[selectedIrisModel]) {
        finalFiber = IRIS_REFERENCE_IMAGES[selectedIrisModel].fiber;
    }

    const key = buildIrisKey(irisColor, finalFiber, pigmentation, rings);
    const profileKey = PROFILE_MAP[key] || 'visionary-adaptive';
    return QUIZ_PROFILES[profileKey] || QUIZ_PROFILES['visionary-adaptive'];
}

/**
 * Gets the 4 reference images to show in Question 5
 * based on the user's previous answers.
 */
export function getFilteredIrisImages(irisColor, fiberPattern, pigmentation, rings) {
    const colorMap = {
        'Azul': 'blue',
        'Verde': 'green',
        'Castanho Claro': 'light-brown',
        'Castanho Escuro': 'dark-brown',
    };
    const c = colorMap[irisColor] || 'blue';

    return Object.values(IRIS_REFERENCE_IMAGES).filter((img) => img.color === c);
}

export const QUIZ_VERSION = '1.1.0';
